import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Box,
  IconButton,
  Grid,
  AppBar,
  Typography,
  Paper,
  Collapse,
  Button,
} from '@mui/material';
import { IconArrowLeft, IconArrowRight, IconCheck, IconSearch, IconX, IconChevronDown, IconChevronUp, IconTrash } from '@tabler/icons-react';
import { useDebounce } from '../Hooks/useDebounce';
import NavBar from '../Navigation/navBar';

interface SizeDetail {
  size: string;
  requested_quantity: number;
  notes: string | null;
  id: number;
}

interface TableRowData {
  styleNo: string;
  brand: string;
  department: string;
  requestedQuantity: number;
  date: string;
  location: string;
  transferred: boolean;
  sizes: SizeDetail[];
}

// New format functions
const formatDisplayDate = (date: string) => {
  const d = new Date(date);
  return new Intl.DateTimeFormat('en-US').format(d); // Displays as 'mm/dd/yyyy'
};

const formatRequestDate = (date: string) => {
  return new Date(date).toISOString().split('T')[0]; // Formats as 'yyyy-mm-dd'
};

const RestockList: React.FC = () => {
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(['', new Date().toISOString().split('T')[0]]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [quantityUpdates, setQuantityUpdates] = useState<Record<number, number>>({});
  useEffect(() => {
    getData();
  }, [page, debouncedSearchTerm, dateRange]);

  const formatDate = (date: string) => {
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  const getData = async () => {
    try {
      const response = await fetch(`/restockListAdmin`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page,
          limit,
          searchTerm,
          startDate: dateRange[0],
          endDate: dateRange[1],
        }),
      });
      const { data, total } = await response.json();
      if (response.status !== 200) console.log('Unable to get the data.');
      setTotalPages(total);

      // Aggregate data by styleNo
      const aggregatedData = data.reduce((acc: Record<string, TableRowData>, item: any) => {
        if (!acc[item.style_no]) {
          acc[item.style_no] = {
            styleNo: item.style_no,
            brand: item.brand,
            department: item.department,
            requestedQuantity: 0,
            date: formatDisplayDate(item.requested_date),
            location: item.dclocation,
            transferred: item.status,
            sizes: [],
          };
        }
        acc[item.style_no].requestedQuantity += item.requested_quantity;
        acc[item.style_no].sizes.push({
          size: item.size,
          requested_quantity: item.requested_quantity,
          notes: item.notes,
          id: item.id,
        });
        return acc;
      }, {});

      setTableData(Object.values(aggregatedData));
    } catch (error) {
      console.error(error);
    }
  };

  const handleStartDateChange = (e: any) => {
    setDateRange([e.target.value, dateRange[1]]);
  };

  const handleEndDateChange = (e: any) => {
    setDateRange([dateRange[0], e.target.value]);
  };

  const handleTimeLineChange = (e: any) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    if (e.target.value === 'This Week') {
      const daysSinceSaturday = today.getDay() === 0 ? 1 : today.getDay() + 1;
      startOfWeek.setDate(today.getDate() - daysSinceSaturday);
      setDateRange([startOfWeek.toISOString().split('T')[0], today.toISOString().split('T')[0]]);
    } else if (e.target.value === 'Last Week') {
      const daysSinceSaturday = today.getDay() === 0 ? 1 : today.getDay() + 1;
      startOfWeek.setDate(today.getDate() - daysSinceSaturday);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() - 1);
      startOfWeek.setDate(endOfWeek.getDate() - 7);
      setDateRange([startOfWeek.toISOString().split('T')[0], endOfWeek.toISOString().split('T')[0]]);
    }
  };

  const toggleRow = (styleNo: string) => {
    setExpandedRow(expandedRow === styleNo ? null : styleNo);
  };

  const handleQuantityChange = (sizeId: number, newQuantity: number) => {
    setQuantityUpdates((prev) => ({ ...prev, [sizeId]: newQuantity }));
  };

  const handleQuantityUpdate = async (sizeId: number) => {
    const newQuantity = quantityUpdates[sizeId];
    
    if (newQuantity <= 0) {
      alert("Quantity must be greater than zero.");
      return;
    }
   
    try {
      const response = await fetch(`/update-size/${sizeId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requested_quantity: newQuantity }),
      });
  
      const result = await response.json();
      if (!response.ok) {
        alert(result.message || 'Failed to update quantity');
        return;
      }
  
      alert(result.message);
  
      // Optionally, you can re-fetch data or update state to reflect the changes
      setTableData((prevData) =>
        prevData.map((item) => ({
          ...item,
          sizes: item.sizes.map((size) =>
            size.id === sizeId ? { ...size, requested_quantity: newQuantity } : size
          ),
        }))
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Error updating quantity');
    }
  };

  const handleSizeDelete = async (styleNo: string, sizeId: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this size?');
    if (!confirmed) return;

    try {
      await fetch(`/delete-size/${sizeId}`, { method: 'DELETE' });
      setTableData((prevData) =>
        prevData
          .map((item) =>
            item.styleNo === styleNo
              ? { ...item, sizes: item.sizes.filter((sizeDetail) => sizeDetail.id !== sizeId) }
              : item
          )
          .filter((item) => item.sizes.length > 0) // Remove the style if no sizes are left
      );
    } catch (error) {
      console.error('Error deleting size:', error);
    }
  };

  const handleStyleDelete = async (styleNo: string, date: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this style and all its sizes?');
    if (!confirmed) return;

    try {
      await fetch(`/delete-style`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styleNo, date: formatRequestDate(date) }),
      });
      setTableData((prevData) => prevData.filter((item) => item.styleNo !== styleNo));
    } catch (error) {
      console.error('Error deleting style:', error);
    }
  };

  return (
    <>
      <AppBar position="sticky">
        <NavBar />
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F1F6F9' }}>
        <TableContainer component={Paper} sx={{ flex: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['Style No', 'Brand', 'Department', 'Quantity', 'Date', 'DC Location', 'Transferred', 'Actions'].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: '#F1F6F9',
                      fontWeight: 'bold',
                      backgroundColor: '#212A3E',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((item) => (
                <React.Fragment key={item.styleNo}>
                  <TableRow sx={{ backgroundColor: '#fff' }}>
                    <TableCell>
                      <IconButton onClick={() => toggleRow(item.styleNo)} size="small">
                        {expandedRow === item.styleNo ? <IconChevronUp /> : <IconChevronDown />}
                      </IconButton>
                      {item.styleNo}
                    </TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.department}</TableCell>
                    <TableCell>{item.requestedQuantity}</TableCell>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>{item.location}</TableCell>
                    <TableCell>{item.transferred ? <IconCheck color="green" /> : <IconX color="red" />}</TableCell>
                    <TableCell>
                      <IconButton color="error" onClick={() => handleStyleDelete(item.styleNo, item.date)}>
                        <IconTrash />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={expandedRow === item.styleNo} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="subtitle1" gutterBottom component="div">
                            Size Details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {['Size', 'Quantity', 'Notes', 'Actions'].map((subHeader) => (
                                  <TableCell key={subHeader} sx={{ fontWeight: 'bold' }}>
                                    {subHeader}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.sizes.map((sizeDetail) => (
                                <TableRow key={sizeDetail.id}>
                                  <TableCell>{sizeDetail.size}</TableCell>
                                  <TableCell>
  <TextField
    type="number"
    size="small"
    value={quantityUpdates[sizeDetail.id] ?? sizeDetail.requested_quantity}
    onChange={(e) => handleQuantityChange(sizeDetail.id, Number(e.target.value))}
  />
</TableCell>
<TableCell>{sizeDetail.notes}</TableCell>
<TableCell>
  <Button
    variant="contained"
    size="small"
    onClick={() => handleQuantityUpdate(sizeDetail.id)}
  >
    Update
  </Button>
</TableCell>
                                  <TableCell>{sizeDetail.notes || '-'}</TableCell>
                                  <TableCell>
                                    <IconButton color="error" onClick={() => handleSizeDelete(item.styleNo, sizeDetail.id)}>
                                      <IconTrash />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom section with search and pagination */}
        <Box
          sx={{
            backgroundColor: '#F1F6F9',
            padding: '10px',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            boxShadow: '0px -1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                placeholder="Search by Style No"
                variant="outlined"
                size="small"
                onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                InputProps={{ startAdornment: <IconSearch /> }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Line</InputLabel>
                <Select label="Time Line" onChange={handleTimeLineChange}>
                  <MenuItem value="This Week">This Week</MenuItem>
                  <MenuItem value="Last Week">Last Week</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Box display="flex" gap={1}>
                <TextField type="date" value={dateRange[0]} onChange={handleStartDateChange} size="small" fullWidth />
                <TextField type="date" value={dateRange[1]} onChange={handleEndDateChange} size="small" fullWidth />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} display="flex" justifyContent="center" alignItems="center">
              <IconButton onClick={() => setPage(page - 1 < 1 ? 1 : page - 1)} disabled={page === 1} color="inherit">
                <IconArrowLeft size={20} />
              </IconButton>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#212A3E', margin: '0 8px' }}>
                {page} of {totalPages}
              </Typography>
              <IconButton onClick={() => setPage(page + 1 > totalPages ? totalPages : page + 1)} disabled={page === totalPages} color="inherit">
                <IconArrowRight size={20} />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default RestockList;
